/**
 * Select — native styled <select>.
 *
 * For richer needs (search/filter/portal), use <Combobox> instead.
 *
 * Design system mapping:
 *   • Box matches Input (mch-input).
 *   • The native <select> opts out of @tailwindcss/forms via the `ui-control`
 *     reset class (defined in application.css). All visual styling comes from
 *     this component's utility classes — no plugin defaults to fight.
 *   • Chevron is a real <svg> sibling so it inherits text color (works in
 *     dark mode) and sits above the select with pointer-events: none.
 *
 * Layout:
 *   The wrapper <span> owns width / margin (className passes through to it).
 *   The <select> inside is always w-full so the chevron's right-* anchor
 *   tracks the visible edge of the control.
 */
import * as React from "react"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

export const selectVariants = cva(
  [
    "ui-control",
    "block w-full appearance-none rounded-md bg-white dark:bg-webex-gray-950",
    "text-sm text-webex-gray-1000 dark:text-white",
    "border transition-colors duration-150 motion-reduce:transition-none",
    "focus:outline-none focus:ring-2 focus:ring-webex-blue-600 focus:ring-offset-1 focus:ring-offset-white dark:focus:ring-offset-webex-gray-950",
    "disabled:cursor-not-allowed disabled:opacity-70 disabled:bg-webex-gray-100 dark:disabled:bg-webex-gray-900",
  ].join(" "),
  {
    variants: {
      state: {
        default: "border-webex-gray-500 dark:border-webex-gray-400",
        error:   "border-webex-red-600 dark:border-webex-red-500 focus:ring-webex-red-600",
      },
      size: {
        // pl smaller than pr so the value text doesn't run under the chevron
        sm:      "h-6 pl-2 pr-7 text-xs",
        default: "h-8 pl-3 pr-8",
        lg:      "h-10 pl-4 pr-9 text-base",
      },
    },
    defaultVariants: {
      state: "default",
      size: "default",
    },
  },
)

export interface SelectOption {
  value: string | number
  label: string
  disabled?: boolean
}

export interface SelectProps
  extends Omit<React.SelectHTMLAttributes<HTMLSelectElement>, "size">,
    VariantProps<typeof selectVariants> {
  options?: SelectOption[]
  placeholder?: string
}

export const Select = React.forwardRef<HTMLSelectElement, SelectProps>(
  ({ className, state, size, options, placeholder, children, ...props }, ref) => {
    const sizeKey = (size ?? "default") as "sm" | "default" | "lg"
    const chevronOffset =
      sizeKey === "sm" ? "right-2" : sizeKey === "lg" ? "right-3" : "right-2.5"
    return (
      <span className={cn("relative inline-block align-middle", className)}>
        <select
          ref={ref}
          className={selectVariants({ state, size })}
          {...props}
        >
          {placeholder && (
            <option value="" disabled hidden>
              {placeholder}
            </option>
          )}
          {options?.map((opt) => (
            <option key={opt.value} value={opt.value} disabled={opt.disabled}>
              {opt.label}
            </option>
          ))}
          {children}
        </select>
        <svg
          aria-hidden="true"
          viewBox="0 0 14 14"
          className={cn(
            "pointer-events-none absolute top-1/2 -translate-y-1/2",
            chevronOffset,
            sizeKey === "sm" ? "size-3" : "size-3.5",
            "text-webex-gray-700 dark:text-webex-gray-300",
          )}
          fill="none"
          stroke="currentColor"
          strokeWidth="1.6"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="3,5.5 7,9.5 11,5.5" />
        </svg>
      </span>
    )
  },
)
Select.displayName = "Select"
